import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Free-shipping progress bar.
 *
 * The cart page and cart drawer re-render server-side on every cart change, so
 * their instances are already correct by the time they hit the DOM. The product
 * page does not, which is the reason this element exists: it listens for
 * `cart:update` (both CartAddEvent and CartUpdateEvent use that event name) and
 * repaints from the cart payload carried on the event.
 *
 * Repainting the cart instances too is harmless — the values it writes are the
 * ones the server is about to render anyway.
 */
class SSShippingBar extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * @param {CustomEvent<{ resource?: { total_price?: number, requires_shipping?: boolean, item_count?: number } }>} event
   */
  #handleCartUpdate = async (event) => {
    let cart = event.detail?.resource;

    // Some dispatch sites pass `{}` (error paths) or a line-item payload rather
    // than the full cart, so fall back to the cart itself when the total is
    // missing. Without this the bar silently freezes at its last value.
    if (typeof cart?.total_price !== 'number') {
      try {
        const response = await fetch(`${Theme?.routes?.cart_url ?? '/cart'}.js`);
        if (!response.ok) return;
        cart = await response.json();
      } catch {
        return;
      }
    }

    if (typeof cart?.total_price !== 'number') return;

    // A cart of nothing but gift cards never ships. Matches the Liquid gate.
    if ((cart.item_count ?? 0) > 0 && cart.requires_shipping === false) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    this.#render(cart.total_price);
  };

  /**
   * @param {number} total - Cart total in minor units.
   */
  #render(total) {
    const threshold = Number(this.dataset.threshold);
    if (!Number.isFinite(threshold) || threshold <= 0) return;

    this.dataset.total = String(total);

    const remaining = threshold - total;
    const percent = Math.min(100, Math.round((total * 100) / threshold));

    const fill = this.querySelector('[data-ss-ship-fill]');
    if (fill instanceof HTMLElement) fill.style.width = `${percent}%`;

    const track = this.querySelector('[data-ss-ship-track]');
    track?.setAttribute('aria-valuenow', String(percent));

    // Only the text node is swapped; the tick icon sits beside it and is shown
    // or hidden by the --qualified class alone.
    const text = this.querySelector('[data-ss-ship-text]');
    if (!text) return;

    if (remaining <= 0) {
      this.classList.add('ss-ship--qualified');
      text.textContent = this.dataset.messageQualified ?? '';
    } else {
      this.classList.remove('ss-ship--qualified');
      text.textContent = (this.dataset.messageAway ?? '').replace('[amount]', this.#money(remaining));
    }
  }

  /**
   * Formats minor units using the shop's money format, dropping a `.00` tail so
   * the copy reads "£22" rather than "£22.00" — matching the
   * `money_without_trailing_zeros` filter used on the server side.
   * @param {number} cents
   * @returns {string}
   */
  #money(cents) {
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'GBP';
    const formatted = formatMoney(cents, format, currency);

    return cents % 100 === 0 ? formatted.replace(/[.,]00\b/, '') : formatted;
  }
}

if (!customElements.get('ss-shipping-bar')) {
  customElements.define('ss-shipping-bar', SSShippingBar);
}
